import React, { useState, useEffect, useCallback } from 'react';
import adminConsoleService, {
  type SecurityGroup,
  type SecuritySubGroup,
} from '@services/adminConsoleService';
import { SecurityGroupCard } from '../securityGroups/SecurityGroupCard';

export const SecurityGroupsTab: React.FC = () =>
{
  const [groups, setGroups] = useState<SecurityGroup[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add new group modal state
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);

  // Add new sub-group modal state
  const [_addSubGroupFor, _setAddSubGroupFor] = useState<number | null>(null);

  // Confirm delete state
  const [confirmDelete, setConfirmDelete] = useState<{
    type: 'group' | 'subgroup';
    id: number;
    name: string;
  } | null>(null);

  const loadGroups = useCallback(() =>
  {
    setLoading(true);
    adminConsoleService
      .getSecurityGroups()
      .then(data => setGroups(data.filter(g => !g.IsDeleted)))
      .catch(() => setError('Failed to load security groups. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() =>
  {
    loadGroups();
  }, [loadGroups]);

  // ── Expand / Collapse ────────────────────────────────────────────────────────
  const handleToggle = useCallback((groupId: number) =>
  {
    setGroups(prev => prev.map(g => (g.GroupId === groupId ? { ...g, IsOpen: !g.IsOpen } : g)));
  }, []);

  // ── Rename Group ─────────────────────────────────────────────────────────────
  const handleRenameGroup = useCallback(
    async (groupId: number, name: string) =>
    {
      setGroups(prev => prev.map(g => (g.GroupId === groupId ? { ...g, Name: name } : g)));
      try
      {
        await adminConsoleService.renameGroup(groupId, name);
      }
      catch
      {
        setError('Failed to rename group.');
        loadGroups();
      }
    },
    [loadGroups],
  );

  // ── Add Group ────────────────────────────────────────────────────────────────
  const handleAddGroup = async () =>
  {
    const name = newGroupName.trim();
    if (!name) return;
    setAddingGroup(true);
    try
    {
      const created = await adminConsoleService.saveGroup(name);
      setGroups(prev => [
        ...prev,
        {
          GroupId: created,
          Name: name,
          IsDeleted: false,
          IsDefault: false,
          IsOpen: false,
          SecurityGroups: [],
          GroupAdminUsers: [],
        },
      ]);
      setShowAddGroup(false);
      setNewGroupName('');
    }
    catch
    {
      setError('Failed to create group.');
    }
    finally
    {
      setAddingGroup(false);
    }
  };

  // ── Delete Group ─────────────────────────────────────────────────────────────
  const handleConfirmDeleteGroup = useCallback(
    (groupId: number) =>
    {
      const group = groups.find(g => g.GroupId === groupId);
      if (!group) return;
      setConfirmDelete({ type: 'group', id: groupId, name: group.Name });
    },
    [groups],
  );

  const executeDeleteGroup = async () =>
  {
    if (!confirmDelete || confirmDelete.type !== 'group') return;
    const { id } = confirmDelete;
    setConfirmDelete(null);
    setGroups(prev => prev.filter(g => g.GroupId !== id));
    try
    {
      await adminConsoleService.deleteGroup(id);
    }
    catch
    {
      setError('Failed to delete group.');
      loadGroups();
    }
  };

  // ── Add Sub-group ────────────────────────────────────────────────────────────
  const handleOpenAddSubGroup = useCallback(() =>
  {
    loadGroups();
  }, []);

  // ── Rename Sub-group ─────────────────────────────────────────────────────────
  const handleRenameSubGroup = useCallback(
    async (subGroupId: number, name: string) =>
    {
      setGroups(prev =>
        prev.map(g => ({
          ...g,
          SecurityGroups: g.SecurityGroups.map(sg =>
            sg.SecurityGroupID === subGroupId ? { ...sg, Name: name } : sg,
          ),
        })),
      );
      try
      {
        await adminConsoleService.updateSecurityGroupName(subGroupId, name);
      }
      catch
      {
        setError('Failed to rename security group.');
        loadGroups();
      }
    },
    [loadGroups],
  );

  // ── Update Sub-group ─────────────────────────────────────────────────────────
  const handleUpdateSubGroup = useCallback(
    (subGroupId: number, updates: Partial<SecuritySubGroup>) =>
    {
      setGroups(prev =>
        prev.map(g => ({
          ...g,
          SecurityGroups: g.SecurityGroups.map(sg =>
            sg.SecurityGroupID === subGroupId ? { ...sg, ...updates } : sg,
          ),
        })),
      );
    },
    [],
  );

  // ── Update Group ─────────────────────────────────────────────────────────────
  const handleUpdateGroup = useCallback((groupId: number, updates: Partial<SecurityGroup>) =>
  {
    setGroups(prev => prev.map(g => (g.GroupId === groupId ? { ...g, ...updates } : g)));
  }, []);

  // ── Delete Sub-group ─────────────────────────────────────────────────────────
  const handleConfirmDeleteSubGroup = useCallback(
    (subGroupId: number) =>
    {
      let name = '';
      for (const g of groups)
      {
        const sg = g.SecurityGroups.find(s => s.SecurityGroupID === subGroupId);
        if (sg)
        {
          name = sg.Name;
          break;
        }
      }
      setConfirmDelete({ type: 'subgroup', id: subGroupId, name });
    },
    [groups],
  );

  const executeDeleteSubGroup = async () =>
  {
    if (!confirmDelete || confirmDelete.type !== 'subgroup') return;
    const { id } = confirmDelete;
    setConfirmDelete(null);
    setGroups(prev =>
      prev.map(g => ({
        ...g,
        SecurityGroups: g.SecurityGroups.map(sg =>
          sg.SecurityGroupID === id ? { ...sg, IsDeleted: true } : sg,
        ),
      })),
    );
    try
    {
      await adminConsoleService.deleteSecurityGroup(id);
    }
    catch
    {
      setError('Failed to delete security group.');
      loadGroups();
    }
  };

  // ── Search filter ────────────────────────────────────────────────────────────
  const filtered = search.trim()
    ? groups
      .map(g => ({
        ...g,
        SecurityGroups: g.SecurityGroups.filter(
          sg =>
            !sg.IsDeleted &&
              (sg.Name.toLowerCase().includes(search.toLowerCase()) ||
                sg.SecurityGroupLayers.some(l =>
                  l.Name.toLowerCase().includes(search.toLowerCase()),
                ) ||
                sg.PeopleSecurityGroup.some(p =>
                  p.Name.toLowerCase().includes(search.toLowerCase()),
                )),
        ),
      }))
      .filter(
        g => g.Name.toLowerCase().includes(search.toLowerCase()) || g.SecurityGroups.length > 0,
      )
    : groups;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full">
      {/* Description */}
      <div className="px-6 pt-5">
        <p className="text-[#62aacb] text-[16px] font-medium leading-snug">Security groups</p>
        <p className="text-[#555] text-xs mt-1">
          Security groups contain users that define permissions and visibility of projects and
          layers. Only Corgee Admins can create and modify security groups.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 pt-5 pb-4 gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-[#555]">Find by keyword:</span>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search security groups..."
            className="border border-[#ccc] rounded px-2 py-1 text-sm text-[#333] outline-none focus:border-[#62aacb] w-[220px]"
          />
        </div>
        <button
          onClick={() =>
          {
            setShowAddGroup(true);
            setNewGroupName('');
          }}
          className="flex items-center gap-1 text-[#62aacb] text-[12px] group"
        >
          <span
            className="material-icons text-[16px] dark:text-[#beb7ac]"
            style={{ fontSize: '16px' }}
          >
            add_circle_outline
          </span>
          <span className="group-hover:underline">Add New Security Group</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-6 mb-3 px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="material-icons text-[14px]">error_outline</span>
            {error}
          </div>
          <button onClick={() => setError(null)} className="text-[#c62828] hover:opacity-70">
            <span className="material-icons text-[14px]">close</span>
          </button>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto border-t border-[#e8e8e8] mx-6 mb-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <span className="material-icons animate-spin text-[#62aacb] text-[32px]">refresh</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-[#aaa] text-sm gap-2">
            <span className="material-icons text-[32px] w-[24px]">group_off</span>
            {search ? 'No security groups match your search.' : 'No security groups found.'}
          </div>
        ) : (
          filtered.map(group => (
            <SecurityGroupCard
              key={group.GroupId}
              group={group}
              onToggle={handleToggle}
              onRenameGroup={handleRenameGroup}
              onDeleteGroup={handleConfirmDeleteGroup}
              onAddSubGroup={handleOpenAddSubGroup}
              onRenameSubGroup={handleRenameSubGroup}
              onDeleteSubGroup={handleConfirmDeleteSubGroup}
              onUpdateSubGroup={handleUpdateSubGroup}
              onUpdateGroup={handleUpdateGroup}
            />
          ))
        )}
      </div>

      {/* ── Add Group Modal ── */}
      {showAddGroup && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/30" onClick={() => setShowAddGroup(false)} />
          <div className="relative z-10 bg-white rounded shadow-xl p-6 w-[360px]">
            <h3 className="text-[15px] font-medium text-[#62aacb] mb-4">Add New Security Group</h3>
            <input
              autoFocus
              type="text"
              value={newGroupName}
              onChange={e => setNewGroupName(e.target.value)}
              onKeyDown={e =>
              {
                if (e.key === 'Enter') handleAddGroup();
                if (e.key === 'Escape') setShowAddGroup(false);
              }}
              placeholder="Security group name"
              className="w-full border border-[#ccc] rounded px-3 py-2 text-sm outline-none focus:border-[#62aacb] mb-4"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddGroup(false)}
                className="px-3 py-1.5 text-[12px] border border-[#ccc] text-[#555] rounded hover:bg-[#f5f5f5]"
              >
                Cancel
              </button>
              <button
                onClick={handleAddGroup}
                disabled={!newGroupName.trim() || addingGroup}
                className="px-3 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] disabled:opacity-50"
              >
                {addingGroup ? 'Adding...' : 'Add Security Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Delete Modal ── */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/30" onClick={() => setConfirmDelete(null)} />
          <div className="relative z-10 bg-white rounded shadow-xl p-6 w-[360px]">
            <h3 className="text-[15px] font-medium text-[#62aacb] mb-2">
              Delete Security Group
            </h3>
            <p className="text-[13px] text-[#555] mb-5">
              Are you sure you want to delete{' '}
              <span className="font-semibold">"{confirmDelete.name}"</span>? This action cannot be
              undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-3 py-1.5 text-[12px] border border-[#ccc] text-[#555] rounded hover:bg-[#f5f5f5]"
              >
                Cancel
              </button>
              <button
                onClick={
                  confirmDelete.type === 'group' ? executeDeleteGroup : executeDeleteSubGroup
                }
                className="px-3 py-1.5 text-[12px] bg-[#e57373] text-white rounded hover:bg-[#c62828]"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
